/**
 * Long Panel tasks that run while the manager keeps working
 * (docs/decisions/2026-10-08-ai-photo-enhancement-runs-in-the-background-of-the-panel.md).
 *
 * The store owns the in-flight promise, so leaving the screen that started a task
 * no longer drops its answer. It lives once per restaurant session (principal and
 * restaurant) and mirrors the task descriptors — never the promises — to the tab's
 * durable work, so a finished result can still be found after a reload while it
 * is valid on the server. A task that was running when the tab reloaded cannot be
 * resumed: it is restored as `interrupted` and the manager may ask again.
 *
 * Framework-free: the React provider and the status chip read it through
 * `subscribe`/`getSnapshot`.
 */

import type { DurableWorkKind, ImmediateDurableWork } from './durable-work';

export type BackgroundTaskKind = 'photo_enhancement';

type BackgroundTaskState = 'running' | 'ready' | 'failed' | 'interrupted';

export interface BackgroundTask {
  /** One task per kind, restaurant and subject (a dish): `kind:restaurantId:subjectId`. */
  readonly id: string;
  readonly kind: BackgroundTaskKind;
  readonly restaurantId: string;
  readonly subjectId: string;
  /** What the manager reads in the chip: the dish name at the moment the task started. */
  readonly subjectName: string;
  /** The editor intent the task's server-side result is bound to. */
  readonly editorIntentId: string;
  readonly startedAt: number;
  readonly state: BackgroundTaskState;
  /** The kind's plain result data once `ready` (persisted; validated by its reader). */
  readonly result: unknown;
  /** When the result stops being usable on the server (epoch ms); `null` until `ready`. */
  readonly expiresAt: number | null;
  /** Plain Spanish for a `failed` task. */
  readonly message: string | null;
  /** True when the chip may run the task again without opening its screen. */
  readonly retryable: boolean;
  /** The manager closed the «lista» chip: the result still waits on its subject. */
  readonly acknowledged: boolean;
}

/** What one run of a task ends in. A failure may carry the exact way to ask again. */
export type BackgroundTaskOutcome =
  | Readonly<{ state: 'ready'; result: unknown; expiresAt: number }>
  | Readonly<{ state: 'failed'; message: string; retry: (() => Promise<BackgroundTaskOutcome>) | null }>;

interface BackgroundTaskStart {
  readonly kind: BackgroundTaskKind;
  readonly restaurantId: string;
  readonly subjectId: string;
  readonly subjectName: string;
  readonly editorIntentId: string;
  readonly run: () => Promise<BackgroundTaskOutcome>;
}

type BackgroundTaskStartResult = 'started' | 'subject_busy' | 'limit';

/**
 * Owner decision 4: at most three tasks run at the same time in one session.
 * @internal Exported for its spec.
 */
export const BACKGROUND_TASK_LIMIT = 3;

export const backgroundTaskId = (kind: BackgroundTaskKind, restaurantId: string, subjectId: string): string =>
  `${kind}:${restaurantId}:${subjectId}`;

export interface BackgroundTaskStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): readonly BackgroundTask[];
  /** Starts a task unless its subject already has one running or waiting for review, or three are running. */
  start(input: BackgroundTaskStart): BackgroundTaskStartResult;
  /** Runs a failed task again with the exact retry its last outcome offered. */
  retry(id: string): boolean;
  /** Hides a finished task's chip: a failure is forgotten, a result keeps waiting on its subject. */
  dismiss(id: string): void;
  /** The result was used or thrown away: the task ends. */
  resolve(id: string): void;
  /** Forgets every result past its server expiry; returns whether anything changed. */
  expire(): boolean;
  /** «Ver»: a request for the subject's screen to open the task (consumed once). */
  requestOpen(id: string): void;
  openRequest(): string | null;
  clearOpenRequest(id: string): void;
}

const isTask = (value: unknown): value is BackgroundTask => {
  if (typeof value !== 'object' || value === null) return false;
  const task = value as Record<string, unknown>;
  return (
    typeof task.id === 'string' &&
    task.kind === 'photo_enhancement' &&
    typeof task.restaurantId === 'string' &&
    typeof task.subjectId === 'string' &&
    typeof task.subjectName === 'string' &&
    typeof task.editorIntentId === 'string' &&
    typeof task.startedAt === 'number' &&
    ['running', 'ready', 'failed', 'interrupted'].includes(task.state as string) &&
    (task.expiresAt === null || typeof task.expiresAt === 'number') &&
    (task.message === null || typeof task.message === 'string') &&
    typeof task.acknowledged === 'boolean'
  );
};

/** The tasks of one session, in the tab. A result ends with its server expiry, well before this. */
export const BACKGROUND_TASK_WORK: DurableWorkKind<BackgroundTask, 'session'> = {
  kind: 'background-task',
  tier: 'session',
  // A proposal the person can ask for again; nothing here is unsent.
  whenOwnerLeaves: 'purge',
  ttlMs: 7 * 24 * 3_600_000,
  accepts: isTask,
};

const readStored = (work: ImmediateDurableWork<BackgroundTask> | null, now: number): BackgroundTask[] =>
  (work?.entriesNow() ?? []).flatMap(({ item: task }) => {
    // The promise did not survive the reload: say so, never pretend it still runs.
    if (task.state === 'running') return [{ ...task, state: 'interrupted' as const, retryable: false, acknowledged: false }];
    if (task.state === 'ready' && (task.expiresAt === null || task.expiresAt <= now)) return [];
    return [{ ...task, retryable: false }];
  });

export function createBackgroundTaskStore(options: {
  /** Where the descriptors survive a reload, under this session's principal and restaurant. Without it, memory only. */
  readonly work?: ImmediateDurableWork<BackgroundTask>;
  readonly now?: () => number;
} = {}): BackgroundTaskStore {
  const now = options.now ?? Date.now;
  const work = options.work ?? null;
  const listeners = new Set<() => void>();
  const retries = new Map<string, () => Promise<BackgroundTaskOutcome>>();
  /** Each run has a token: an answer for a task that was resolved or restarted is dropped. */
  const runs = new Map<string, number>();
  let runCounter = 0;
  let tasks: readonly BackgroundTask[] = readStored(work, now());
  let open: string | null = null;

  const persist = (previous: readonly BackgroundTask[]) => {
    if (work === null) return;
    for (const task of previous) if (!tasks.some((entry) => entry.id === task.id)) work.removeNow(task.id);
    for (const task of tasks) if (!previous.includes(task)) work.putNow(task.id, task);
  };
  const publish = (next: readonly BackgroundTask[]) => {
    const previous = tasks;
    tasks = next;
    persist(previous);
    for (const listener of [...listeners]) listener();
  };
  const update = (id: string, patch: Partial<BackgroundTask>) =>
    publish(tasks.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  const forget = (id: string) => {
    retries.delete(id);
    runs.delete(id);
    if (open === id) open = null;
    publish(tasks.filter((task) => task.id !== id));
  };

  const execute = (id: string, run: () => Promise<BackgroundTaskOutcome>) => {
    const token = ++runCounter;
    runs.set(id, token);
    retries.delete(id);
    void Promise.resolve()
      .then(run)
      .catch(
        (): BackgroundTaskOutcome => ({
          state: 'failed',
          message: 'No se ha podido terminar. No se ha cambiado nada.',
          retry: run,
        }),
      )
      .then((outcome) => {
        if (runs.get(id) !== token || !tasks.some((task) => task.id === id)) return;
        runs.delete(id);
        if (outcome.state === 'ready') {
          update(id, {
            state: 'ready',
            result: outcome.result,
            expiresAt: outcome.expiresAt,
            message: null,
            retryable: false,
            acknowledged: false,
          });
          return;
        }
        if (outcome.retry) retries.set(id, outcome.retry);
        update(id, {
          state: 'failed',
          result: null,
          expiresAt: null,
          message: outcome.message,
          retryable: outcome.retry !== null,
          acknowledged: false,
        });
      });
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => tasks,
    start(input) {
      const id = backgroundTaskId(input.kind, input.restaurantId, input.subjectId);
      const current = tasks.find((task) => task.id === id);
      if (current && (current.state === 'running' || current.state === 'ready')) return 'subject_busy';
      if (tasks.filter((task) => task.state === 'running').length >= BACKGROUND_TASK_LIMIT) return 'limit';
      const task: BackgroundTask = {
        id,
        kind: input.kind,
        restaurantId: input.restaurantId,
        subjectId: input.subjectId,
        subjectName: input.subjectName,
        editorIntentId: input.editorIntentId,
        startedAt: now(),
        state: 'running',
        result: null,
        expiresAt: null,
        message: null,
        retryable: false,
        acknowledged: false,
      };
      if (open === id) open = null;
      publish([...tasks.filter((entry) => entry.id !== id), task]);
      execute(id, input.run);
      return 'started';
    },
    retry(id) {
      const task = tasks.find((entry) => entry.id === id);
      const again = retries.get(id);
      if (!task || task.state !== 'failed' || !again) return false;
      if (tasks.filter((entry) => entry.state === 'running').length >= BACKGROUND_TASK_LIMIT) return false;
      update(id, { state: 'running', message: null, retryable: false, startedAt: now(), acknowledged: false });
      execute(id, again);
      return true;
    },
    dismiss(id) {
      const task = tasks.find((entry) => entry.id === id);
      if (!task || task.state === 'running') return;
      if (task.state === 'ready') update(id, { acknowledged: true });
      else forget(id);
    },
    resolve: (id) => {
      if (tasks.some((task) => task.id === id)) forget(id);
    },
    expire() {
      const at = now();
      const expired = tasks.filter((task) => task.state === 'ready' && task.expiresAt !== null && task.expiresAt <= at);
      for (const task of expired) forget(task.id);
      return expired.length > 0;
    },
    requestOpen(id) {
      if (!tasks.some((task) => task.id === id)) return;
      open = id;
      for (const listener of [...listeners]) listener();
    },
    openRequest: () => open,
    clearOpenRequest(id) {
      if (open === id) open = null;
    },
  };
}
